import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";

function splitSections(obj) {
  if (Array.isArray(obj)) return obj.map(s => String(s).trim()).filter(Boolean);
  if (obj && Array.isArray(obj.sections)) return obj.sections.map(s => String(s).trim()).filter(Boolean);
  return String(obj || "").split(",").map(s => s.trim()).filter(Boolean);
}
function formatName(s) {
  const last = String(s.lastName || "").trim();
  const first = String(s.firstName || "").trim();
  const middle = String(s.middleName || s.middleInitial || "").trim();
  const core = `${last}${last && first ? ", " : ""}${first}`;
  return middle ? `${core} ${middle}` : core;
}

export default function PrintList() {
  const [params] = useSearchParams();
  const user = getCurrentUser();
  const role = String(user?.role || "").toLowerCase();
  const campusParam = (params.get("campus") || "").toLowerCase();
  const campus = campusParam || (role === "admin" || role === "superadmin" ? "" : String(user?.campus || "").toLowerCase());
  const grade = params.get("grade") || "";
  const section = params.get("section") || "";
  const view = params.get("view") || "students";
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      let q = collection(db, "students");
      if (campus) q = query(q, where("campus", "==", campus));
      if (grade) q = query(q, where("gradeLevel", "==", grade));
      if (section) q = query(q, where("sections", "array_contains", section));
      q = query(q, orderBy("lastNameLower"), orderBy("firstNameLower"));
      const snap = await getDocs(q);
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => { try { window.print(); } catch (e) { /* ignore */ } }, 500);
    })();
  }, [campus, grade, section, view]);

  const filtered = useMemo(() => {
    let r = rows;
    if (view === "noEmail") r = r.filter(s => !(s.studentEmail || s.email));
    if (view === "shsStrand") r = r.filter(s => /grade\s?1[1-2]/i.test(String(s.gradeLevel || "")));
    const genderRank = (g) => {
      const s = String(g || "").toLowerCase();
      if (s.startsWith("m")) return 0;
      if (s.startsWith("f")) return 1;
      return 2;
    };
    return r.slice().sort((a,b) => {
      const ga = genderRank(a.gender), gb = genderRank(b.gender);
      if (ga !== gb) return ga - gb;
      return formatName(a).localeCompare(formatName(b));
    });
  }, [rows, view]);

  const headersMap = {
    students: ["Full Name","Gender","Grade","Section","Student Email"],
    contacts: ["Full Name","Grade","Section","Guardian","Contact","Email"],
    lrn: ["Full Name","Grade","Section","LRN"],
    noEmail: ["Full Name","Grade","Section"],
    shsStrand: ["Full Name","Grade","Section","Strand"]
  };
  const headers = headersMap[view] || headersMap.students;

  return (
    <div className="container-fluid p-3">
      <h5>Students — {campus ? campus.toUpperCase() : "ALL"} {grade ? `• ${grade}` : ""}{section ? ` • ${section}` : ""}</h5>
      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id}>
                {view === "contacts" && (<>
                  <td>{formatName(s)}</td><td>{s.gradeLevel||""}</td><td>{splitSections(s.section).join(", ")}</td>
                  <td>{s.guardianName || s.parentName || ""}</td><td>{s.guardianContact || s.parentContact || s.fatherContact || s.motherContact || ""}</td>
                  <td>{s.guardianEmail || s.parentEmail || s.fatherEmail || s.motherEmail || ""}</td>
                </>)}
                {view === "lrn" && (<>
                  <td>{formatName(s)}</td><td>{s.gradeLevel||""}</td><td>{splitSections(s.section).join(", ")}</td><td>{s.lrn||""}</td>
                </>)}
                {view === "noEmail" && (<>
                  <td>{formatName(s)}</td><td>{s.gradeLevel||""}</td><td>{splitSections(s.section).join(", ")}</td>
                </>)}
                {view === "shsStrand" && (<>
                  <td>{formatName(s)}</td><td>{s.gradeLevel||""}</td><td>{splitSections(s.section).join(", ")}</td><td>{s.strand||""}</td>
                </>)}
                {view === "students" && (<>
                  <td>{formatName(s)}</td><td className="text-capitalize">{s.gender||""}</td><td>{s.gradeLevel||""}</td><td>{splitSections(s.section).join(", ")}</td><td>{s.studentEmail||""}</td>
                </>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`@media print { @page { size: letter portrait; margin: 12mm; } }`}</style>
    </div>
  );
}
